import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Compass, Trash2 } from "lucide-react";
import { SchoolBrandHeader } from "@/components/school/SchoolBrandHeader";
import { SchoolFitAssessmentPanel } from "@/components/school/SchoolFitAssessmentPanel";
import { SchoolProgramDetailsPanel } from "@/components/school/SchoolProgramDetailsPanel";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SchoolNewsCard } from "@/components/school/SchoolNewsCard";
import { SchoolNotesPanel } from "@/components/school/SchoolNotesPanel";
import { useIsMobile } from "@/hooks/use-mobile";
import type { PipelineEntry, PipelineStage } from "@/services/pipeline";
import {
  fetchCollege,
  fetchSchoolById,
  type CollegeDetail,
  type SchoolResult,
} from "@/services/schools";
import { PIPELINE_STAGES, nextRelationshipAction, stageIndex, stageLabel } from "./pipelineDesign";

interface SchoolPortfolioDetailProps {
  entry: PipelineEntry | null;
  notes: string;
  onClose: () => void;
  onStageChange: (entry: PipelineEntry, stage: PipelineStage) => void;
  onRefreshFit: (entry: PipelineEntry) => void;
  onSaveNotes: (entry: PipelineEntry, notes: string) => Promise<void> | void;
  onRemove: (entry: PipelineEntry) => void;
}

function StageRail({ stage }: { stage: PipelineStage }) {
  const current = stageIndex(stage);
  return (
    <div>
      <div className="mt-4 flex items-center">
        {PIPELINE_STAGES.map((item, index) => (
          <span
            key={item.id}
            className={`relative h-1 flex-1 first:rounded-l-full last:rounded-r-full ${index <= current ? "bg-primary" : "bg-muted"}`}
          >
            {index === current && (
              <span className="absolute -right-1.5 -top-1 block h-3 w-3 rounded-full border-2 border-white bg-forest shadow-[0_0_0_1px_#173127]" />
            )}
          </span>
        ))}
      </div>
      <div className="mt-2 grid grid-cols-5 text-center text-[11px] font-bold text-subtle">
        {PIPELINE_STAGES.map(item => (
          <span key={item.id}>{item.shortLabel}</span>
        ))}
      </div>
    </div>
  );
}

function fallbackProgram(entry: PipelineEntry): SchoolResult {
  return {
    schoolId: entry.school.teamId || entry._id,
    schoolName: entry.school.schoolName,
    college_id: entry.school.collegeId || undefined,
    gender: entry.school.gender || "",
    association: null,
    division: entry.school.division,
    conference: entry.school.conference,
    state: entry.school.state,
    city: entry.school.city,
    region: null,
    rank: null,
    scoring_avg: null,
    headCoach: entry.school.headCoach,
    headCoachEmail: entry.school.headCoachEmail,
    team_url: null,
    logoUrl: entry.school.logoUrl,
    brandColorPrimary: entry.school.brandColorPrimary,
    brandColorSecondary: entry.school.brandColorSecondary,
  };
}

function DetailBody({
  entry,
  notes,
  onStageChange,
  onRefreshFit,
  onSaveNotes,
  onRemove,
  onClose,
}: SchoolPortfolioDetailProps & { entry: PipelineEntry }) {
  const navigate = useNavigate();
  const [programSchool, setProgramSchool] = useState<SchoolResult | null>(null);
  const [collegeData, setCollegeData] = useState<CollegeDetail | null>(null);
  const [programLoading, setProgramLoading] = useState(false);

  useEffect(() => {
    const teamId = entry.school.teamId;
    let cancelled = false;
    setProgramSchool(null);
    setCollegeData(null);
    if (!teamId) {
      setProgramLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setProgramLoading(true);
    async function loadProgram() {
      try {
        const school = await fetchSchoolById(teamId!);
        if (cancelled) return;
        setProgramSchool(school);
        if (school.college_id) {
          const college = await fetchCollege(school.college_id);
          if (!cancelled) setCollegeData(college);
        }
      } catch (error) {
        console.error("Failed to load program details:", error);
      } finally {
        if (!cancelled) setProgramLoading(false);
      }
    }
    void loadProgram();
    return () => {
      cancelled = true;
    };
  }, [entry._id, entry.school.teamId]);

  return (
    <div className="bg-surface">
      <SchoolBrandHeader school={entry.school} fit={entry.golfFit} variant="detail" />
      <div className="space-y-3 p-4 sm:p-5">
        <SchoolFitAssessmentPanel
          fit={entry.golfFit}
          onRefresh={() => onRefreshFit(entry)}
          schoolName={entry.school.schoolName}
          onAskCoach={() => {
            onClose();
            navigate("/chat", {
              state: {
                autoPrompt: `I want to learn more about ${entry.school.schoolName} and its college golf program. Using my profile, the school's information, and my existing fit assessment, give me a recruiting-focused overview of its competitive level, recruiting patterns, roster and coaching considerations, how my game may fit, and the most important questions I should research or ask the coach. Clearly separate verified information from anything I still need to confirm.`,
              },
            });
          }}
        />

        {entry.recommendationContext === "perspective" && (
          <aside className="flex items-start gap-3 rounded-2xl border border-lime-border bg-lime-soft p-4 text-forest-soft">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-white text-primary-text shadow-sm">
              <Compass className="h-4 w-4" />
            </span>
            <div>
              <strong className="block font-display text-base font-extrabold tracking-[-0.025em]">
                A perspective pick
              </strong>
              <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                {entry.recommendationReason ||
                  "This program broadens one of your preferences while keeping golf fit credible."}
              </p>
            </div>
          </aside>
        )}

        {entry.recommendationContext === "exploration" && (
          <aside className="flex items-start gap-3 rounded-2xl border border-border bg-surface p-4 text-forest-soft">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-white text-primary-text shadow-sm">
              <Compass className="h-4 w-4" />
            </span>
            <div>
              <strong className="block font-display text-base font-extrabold tracking-[-0.025em]">
                A program to explore
              </strong>
              <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                {entry.recommendationReason}
              </p>
            </div>
          </aside>
        )}

        <SchoolProgramDetailsPanel
          school={programSchool || fallbackProgram(entry)}
          userGenderLabel={entry.school.gender || "All"}
          collegeData={collegeData}
          collegeLoading={programLoading}
        />

        <SchoolNewsCard teamId={entry.school.teamId} />

        <section className="rounded-2xl border border-border bg-white p-4">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-subtle">
            Relationship
          </p>
          <div className="mt-1.5 flex items-center justify-between gap-3">
            <h3 className="font-display text-xl font-extrabold tracking-[-0.035em] text-forest">
              {stageLabel(entry.stage)}
            </h3>
            <Select
              value={entry.stage}
              onValueChange={value => onStageChange(entry, value as PipelineStage)}
            >
              <SelectTrigger className="h-8 w-[138px] rounded-lg text-[11px] font-bold">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PIPELINE_STAGES.map(item => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <StageRail stage={entry.stage} />
          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div className="rounded-xl border border-border p-3">
              <span className="block text-xs font-semibold text-subtle">Next action</span>
              <strong className="mt-1 block text-[11px] leading-snug">
                {nextRelationshipAction(entry)}
              </strong>
            </div>
            <div className="rounded-xl border border-border p-3">
              <span className="block text-xs font-semibold text-subtle">Academics</span>
              <strong className="mt-1 block text-[11px] leading-snug">Review separately</strong>
            </div>
          </div>
        </section>

        <SchoolNotesPanel
          schoolId={entry.school.teamId || entry._id}
          notes={notes}
          onSave={value => onSaveNotes(entry, value)}
        />

        <button
          type="button"
          onClick={() => onRemove(entry)}
          className="mx-auto flex items-center gap-1.5 pb-4 text-[11px] font-bold text-warn hover:underline"
        >
          <Trash2 className="h-3 w-3" /> Remove from My Schools
        </button>
      </div>
    </div>
  );
}

export function SchoolPortfolioDetail(props: SchoolPortfolioDetailProps) {
  const isMobile = useIsMobile();
  const { entry, onClose } = props;

  if (isMobile) {
    return (
      <Drawer
        open={!!entry}
        onOpenChange={open => {
          if (!open) onClose();
        }}
      >
        <DrawerContent className="max-h-[92vh] overflow-hidden rounded-t-[24px] border-border bg-surface">
          <DrawerHeader className="sr-only">
            <DrawerTitle>{entry?.school.schoolName || "School details"}</DrawerTitle>
            <DrawerDescription>Golf fit and recruiting relationship details</DrawerDescription>
          </DrawerHeader>
          <div className="min-h-0 overflow-y-auto">
            {entry && <DetailBody {...props} entry={entry} />}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Sheet
      open={!!entry}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="flex w-full max-w-[500px] flex-col overflow-hidden border-l-0 bg-surface p-0 sm:max-w-[500px]"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>{entry?.school.schoolName || "School details"}</SheetTitle>
          <SheetDescription>Golf fit and recruiting relationship details</SheetDescription>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {entry && <DetailBody {...props} entry={entry} />}
        </div>
      </SheetContent>
    </Sheet>
  );
}
